import { useMemo, useState } from 'react';
import {
  AlertCircle,
  TrendingDown,
  AlertTriangle,
  Activity,
  TrendingUp,
  CheckCircle,
  RefreshCw,
  Building2,
  ChevronDown,
  ArrowRight,
  Boxes,
  Layers3,
  Target,
  Truck,
  Warehouse,
} from 'lucide-react';
import { useProductsGrouped } from '../shared/hooks/useProducts';
import { useSettings } from '../shared/hooks/useSettings';
import { ProductWithCalculations, StatusType } from '../shared/types/inventory';
import { HorizontalBarChart } from '../components/charts/HorizontalBarChart';
import { StatusDistribution } from '../components/charts/StatusDistribution';
import { InventoryAnalysisModal, UNINFORMED_SUPPLIER_FILTER, UNINFORMED_SUPPLIER_LABEL, type CoverageAnalysisFilter, type InventoryAnalysisFilter, type InventoryAnalysisItem } from '../components/InventoryAnalysisModal';

import { SellerPageLoader } from '../shared/components/SellerPageLoader';
import { CardHelp } from '../shared/components/CardHelp';
const STATUS_PALETTE: Record<StatusType, string> = {
  ruptura: '#dc2626',
  sem_venda: '#64748b',
  iminente: '#ea580c',
  minimo: '#ca8a04',
  critica: '#0891b2',
  excesso: '#2563eb',
  saudavel: '#16a34a',
};

const STATUS_META: Record<StatusType, { label: string; icon: typeof AlertCircle; bgClass: string; textClass: string }> = {
  ruptura: { label: 'Ruptura Instalada', icon: AlertCircle, bgClass: 'bg-red-50 border-red-100', textClass: 'text-red-700' },
  sem_venda: { label: 'Sem venda (avaliar)', icon: Boxes, bgClass: 'bg-slate-50 border-slate-200', textClass: 'text-slate-700' },
  iminente: { label: 'Ruptura Iminente', icon: TrendingDown, bgClass: 'bg-orange-50 border-orange-100', textClass: 'text-orange-700' },
  minimo: { label: 'Perto do Mínimo', icon: AlertTriangle, bgClass: 'bg-yellow-50 border-yellow-100', textClass: 'text-yellow-700' },
  critica: { label: 'Cobertura Crítica', icon: Activity, bgClass: 'bg-cyan-50 border-cyan-100', textClass: 'text-cyan-700' },
  excesso: { label: 'Excesso', icon: TrendingUp, bgClass: 'bg-blue-50 border-blue-100', textClass: 'text-blue-700' },
  saudavel: { label: 'Saudável', icon: CheckCircle, bgClass: 'bg-green-50 border-green-100', textClass: 'text-green-700' },
};

const STATUS_ORDER: StatusType[] = ['ruptura', 'sem_venda', 'iminente', 'minimo', 'critica', 'excesso', 'saudavel'];
const UNIT_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const formatUnits = (value: number) => UNIT_FORMATTER.format(value);

function formatCNPJ(cnpj: string): string {
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return cnpj;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

function calculateMedian(values: number[]) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

type RankedProduct = ProductWithCalculations & { scopeCount: number };
type ConsolidatedProduct = RankedProduct & {
  suppliers: Set<string>;
  weightedLeadTime: number;
  leadTimeWeight: number;
  leadTimeSum: number;
};

function getConsolidatedStatus(product: Pick<ProductWithCalculations, 'estoqueAtual' | 'cmd' | 'ss' | 'rop' | 'cobertura'>, leadTime: number, targetCoverage: number): StatusType {
  if (product.estoqueAtual <= 0) return 'ruptura';
  if (product.cmd <= 0) return 'sem_venda';
  if (product.estoqueAtual < product.ss) return 'iminente';
  if (product.estoqueAtual <= product.rop) return 'minimo';
  if (product.cobertura < leadTime) return 'critica';
  if (product.cobertura > targetCoverage) return 'excesso';
  return 'saudavel';
}

function consolidateRankingProducts(products: ProductWithCalculations[], targetCoverage: number): RankedProduct[] {
  const byEan = new Map<string, ConsolidatedProduct>();
  for (const product of products) {
    const key = product.ean.trim() || product.nome;
    const supplier = product.fornecedor?.trim() || UNINFORMED_SUPPLIER_LABEL;
    const current = byEan.get(key);
    if (!current) {
      byEan.set(key, {
        ...product,
        scopeCount: 1,
        suppliers: new Set([supplier]),
        weightedLeadTime: (product.leadTime || 0) * product.cmd,
        leadTimeWeight: product.cmd,
        leadTimeSum: product.leadTime || 0,
      });
      continue;
    }
    current.scopeCount++;
    current.suppliers.add(supplier);
    current.estoqueAtual += product.estoqueAtual;
    current.vendasPeriodo += product.vendasPeriodo;
    current.cmd += product.cmd;
    current.ddlt += product.ddlt;
    current.ss += product.ss;
    current.rop += product.rop;
    current.estoqueAlvo += product.estoqueAlvo;
    current.pedidoSugerido += product.pedidoSugerido;
    current.weightedLeadTime += (product.leadTime || 0) * product.cmd;
    current.leadTimeWeight += product.cmd;
    current.leadTimeSum += product.leadTime || 0;
  }

  return Array.from(byEan.values()).map(({suppliers, weightedLeadTime, leadTimeWeight, leadTimeSum, ...product}) => {
    const leadTime = leadTimeWeight > 0 ? weightedLeadTime / leadTimeWeight : leadTimeSum / product.scopeCount;
    const cobertura = product.cmd > 0 ? product.estoqueAtual / product.cmd : 0;
    const status = getConsolidatedStatus({...product, cobertura}, leadTime, targetCoverage);
    return {
      ...product,
      leadTime,
      cobertura,
      fornecedor: suppliers.size === 1 ? Array.from(suppliers)[0] : `${suppliers.size} fornecedores`,
      status,
      statusLabel: STATUS_META[status].label,
    };
  });
}

function computeAnalytics(products: ProductWithCalculations[], diasCoberturaAlvo: number, consolidateRankings = false) {
  const counts: Record<StatusType, number> = {
    ruptura: 0, sem_venda: 0, iminente: 0, minimo: 0, critica: 0, excesso: 0, saudavel: 0,
  };
  const coverageBuckets: Array<{key: CoverageAnalysisFilter; label: string; description: string; help: string; value: number; color: string}> = [
    {key: 'no_consumption', label: 'Sem consumo', description: 'CMD igual a zero', help: 'CMD = vendas no período ÷ dias do período. Esta faixa contém produtos com CMD igual a zero; eles não entram no cálculo da cobertura típica.', value: 0, color: '#94a3b8'},
    {key: 'stockout', label: 'Sem estoque', description: 'Com consumo e estoque zerado', help: 'Produtos com CMD maior que zero e estoque atual igual a zero. Itens sem consumo permanecem exclusivamente na faixa Sem consumo.', value: 0, color: '#dc2626'},
    {key: 'below_lead', label: 'Abaixo do lead time', description: 'Risco antes da próxima entrega', help: 'Produtos com CMD maior que zero, estoque positivo e cobertura menor que o lead time. Cobertura = estoque atual ÷ CMD.', value: 0, color: '#ea580c'},
    {key: 'below_target', label: 'Abaixo da meta', description: 'Entre o lead time e a cobertura alvo', help: `Produtos com cobertura maior ou igual ao lead time e menor que a meta configurada de ${diasCoberturaAlvo} dias.`, value: 0, color: '#ca8a04'},
    {key: 'on_target', label: 'Cobertura adequada', description: 'Dentro da faixa planejada', help: `Produtos com cobertura entre a meta de ${diasCoberturaAlvo} dias e 150% dessa meta, inclusive.`, value: 0, color: '#16a34a'},
    {key: 'high', label: 'Cobertura elevada', description: 'Acima de 150% da meta', help: `Produtos com cobertura superior a 150% da meta, ou seja, acima de ${(diasCoberturaAlvo * 1.5).toFixed(1)} dias.`, value: 0, color: '#2563eb'},
  ];
  const suppliers = new Map<string, {
    name: string; filterValue: string; products: number; alerts: number; suggested: number;
    stock: number; coverageSum: number; coverageCount: number; coverageValues: number[];
  }>();
  const coverageValues: number[] = [];
  let totalStock = 0;
  let totalTarget = 0;
  let totalSuggested = 0;
  let coverageSum = 0;
  let coverageCount = 0;
  let excessUnits = 0;
  let zeroStockCount = 0;
  let noSalesCount = 0;
  let belowLeadTimeCount = 0;

  for (const product of products) {
    counts[product.status]++;
    totalStock += product.estoqueAtual;
    totalTarget += product.estoqueAlvo;
    totalSuggested += product.pedidoSugerido;
    excessUnits += Math.max(0, product.estoqueAtual - product.estoqueAlvo);
    if (product.estoqueAtual <= 0) zeroStockCount++;
    if (product.cmd <= 0) noSalesCount++;

    const leadTime = product.leadTime ?? 0;
    if (product.cmd > 0) {
      coverageSum += product.cobertura;
      coverageCount++;
      coverageValues.push(product.cobertura);
      if (product.estoqueAtual > 0 && product.cobertura < leadTime) belowLeadTimeCount++;
    }

    if (product.cmd <= 0) coverageBuckets[0].value++;
    else if (product.estoqueAtual <= 0) coverageBuckets[1].value++;
    else if (product.cobertura < leadTime) coverageBuckets[2].value++;
    else if (product.cobertura < diasCoberturaAlvo) coverageBuckets[3].value++;
    else if (product.cobertura <= diasCoberturaAlvo * 1.5) coverageBuckets[4].value++;
    else coverageBuckets[5].value++;

    const supplierName = product.fornecedor?.trim() || UNINFORMED_SUPPLIER_LABEL;
    const supplierFilter = product.fornecedor?.trim() || UNINFORMED_SUPPLIER_FILTER;
    const supplier = suppliers.get(supplierFilter) || {
      name: supplierName, filterValue: supplierFilter, products: 0, alerts: 0, suggested: 0,
      stock: 0, coverageSum: 0, coverageCount: 0, coverageValues: [],
    };
    supplier.products++;
    supplier.stock += product.estoqueAtual;
    supplier.suggested += product.pedidoSugerido;
    if (product.status === 'ruptura' || product.status === 'iminente' || product.status === 'minimo') supplier.alerts++;
    if (product.cmd > 0) {
      supplier.coverageSum += product.cobertura;
      supplier.coverageCount++;
      supplier.coverageValues.push(product.cobertura);
    }
    suppliers.set(supplierFilter, supplier);
  }

  coverageValues.sort((a, b) => a - b);
  const medianCoverage = coverageValues.length
    ? coverageValues.length % 2
      ? coverageValues[Math.floor(coverageValues.length / 2)]
      : (coverageValues[coverageValues.length / 2 - 1] + coverageValues[coverageValues.length / 2]) / 2
    : 0;
  const avgCoverage = coverageCount > 0 ? coverageSum / coverageCount : 0;
  const alertCount = counts.ruptura + counts.iminente + counts.minimo;
  const healthPct = products.length > 0 ? ((counts.saudavel / products.length) * 100) : 0;
  const fillRate = totalTarget > 0 ? (totalStock / totalTarget) * 100 : 0;
  const riskPoints = counts.ruptura + counts.iminente * 0.8 + counts.minimo * 0.55
    + counts.critica * 0.35 + counts.excesso * 0.2;
  const operationalScore = products.length
    ? Math.max(0, Math.round(100 - (riskPoints / products.length) * 100))
    : 0;

  const rankingProducts = consolidateRankings
    ? consolidateRankingProducts(products, diasCoberturaAlvo)
    : products.map((product) => ({...product, scopeCount: 1}));
  const topLowCoverage = products.map((product) => ({...product, scopeCount: 1}))
    .filter((product) => product.cmd > 0)
    .sort((a, b) => a.cobertura - b.cobertura)
    .slice(0, 8);
  const topHighOrder = [...rankingProducts]
    .filter((product) => product.pedidoSugerido > 0)
    .sort((a, b) => b.pedidoSugerido - a.pedidoSugerido)
    .slice(0, 8);
  const supplierInsights = Array.from(suppliers.values())
    .map((supplier) => ({
      ...supplier,
      alertPct: supplier.products ? (supplier.alerts / supplier.products) * 100 : 0,
      avgCoverage: supplier.coverageCount ? supplier.coverageSum / supplier.coverageCount : 0,
      medianCoverage: calculateMedian(supplier.coverageValues),
    }))
    .sort((a, b) => b.alerts - a.alerts || b.suggested - a.suggested)
    .slice(0, 8);

  return {
    counts, totalStock, totalTarget, totalSuggested, avgCoverage, medianCoverage,
    alertCount, healthPct, fillRate, operationalScore, excessUnits, zeroStockCount,
    noSalesCount, belowLeadTimeCount, stockGap: totalSuggested,
    coverageBuckets, supplierInsights, topLowCoverage, topHighOrder,
  };
}

type DashboardAnalytics = ReturnType<typeof computeAnalytics>;
type CnpjPerformance = {
  cnpj: string;
  razaoSocial: string;
  products: number;
  stock: number;
  targetStock: number;
  stockoutWithConsumption: number;
  stockoutPct: number;
};

export const DashboardPage = () => {
  const { settings } = useSettings();
  const { groups, allProducts, loading, error, refetch } = useProductsGrouped();
  const [selectedCNPJ, setSelectedCNPJ] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [analysisRequest, setAnalysisRequest] = useState<{mode: 'coverage' | 'orders'; initialFilter: InventoryAnalysisFilter; initialSupplier?: string; initialStatuses?: StatusType[]; scopeLabel?: string} | null>(null);
  const openAnalysis = (mode: 'coverage' | 'orders', initialFilter: InventoryAnalysisFilter = 'all', initialSupplier?: string) => setAnalysisRequest({mode, initialFilter, initialSupplier});
  const openStatusAnalysis = (initialStatuses: StatusType[], scopeLabel: string) => setAnalysisRequest({
    mode: 'coverage',
    initialFilter: 'all',
    initialStatuses,
    scopeLabel,
  });

  const hasMultipleCNPJs = groups.length > 1;


  const activeProducts = useMemo(() => {
    if (!selectedCNPJ) return allProducts;
    const group = groups.find((g) => g.cnpj === selectedCNPJ);
    return group ? group.products : allProducts;
  }, [groups, allProducts, selectedCNPJ]);

  const analysisProducts = useMemo<InventoryAnalysisItem[]>(() => {
    if (selectedCNPJ) {
      const group = groups.find((item) => item.cnpj === selectedCNPJ);
      return group ? group.products.map((product) => ({ ...product, cnpj: group.cnpj, razaoSocial: group.razaoSocial || '' })) : [];
    }
    return groups.flatMap((group) => group.products.map((product) => ({ ...product, cnpj: group.cnpj, razaoSocial: group.razaoSocial || '' })));
  }, [groups, selectedCNPJ]);

  const selectedGroup = useMemo(
    () => groups.find((g) => g.cnpj === selectedCNPJ),
    [groups, selectedCNPJ]
  );

  const analytics = useMemo(
    () => computeAnalytics(activeProducts, settings.diasCoberturaAlvo, !selectedCNPJ),
    [activeProducts, selectedCNPJ, settings.diasCoberturaAlvo]
  );
  const averageLeadTime = useMemo(() => {
    if (!activeProducts.length) return settings.leadTime;
    return activeProducts.reduce(
      (sum, product) => sum + (product.leadTime ?? settings.leadTime),
      0
    ) / activeProducts.length;
  }, [activeProducts, settings.leadTime]);
  const cnpjPerformance = useMemo<CnpjPerformance[]>(() => groups.map((group) => {
    const performance = computeAnalytics(group.products, settings.diasCoberturaAlvo)
    const stockoutWithConsumption = performance.coverageBuckets.find((bucket) => bucket.key === 'stockout')?.value || 0
    return {
      cnpj: group.cnpj,
      razaoSocial: group.razaoSocial || '',
      products: group.products.length,
      stock: performance.totalStock,
      targetStock: performance.totalTarget,
      stockoutWithConsumption,
      stockoutPct: group.products.length ? (stockoutWithConsumption / group.products.length) * 100 : 0,
    }
  }).sort((a, b) => b.stockoutWithConsumption - a.stockoutWithConsumption || b.products - a.products), [groups, settings.diasCoberturaAlvo])

  const executiveHeadline = analytics.counts.ruptura > 0
    ? `${analytics.counts.ruptura} produtos estão sem estoque e exigem ação imediata.`
    : analytics.belowLeadTimeCount > 0
      ? `${analytics.belowLeadTimeCount} produtos possuem cobertura menor que o prazo de entrega.`
      : analytics.alertCount > 0
        ? `${analytics.alertCount} produtos precisam de acompanhamento para evitar ruptura.`
        : 'O estoque monitorado está operando sem alertas urgentes.'

  if (loading) {
    return (
      <SellerPageLoader
        title={'Montando a vis\u00e3o geral'}
        description="Consolidando indicadores, alertas e cobertura do estoque."
      />
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <p className="text-gray-900 font-semibold mb-1">Erro ao carregar dados</p>
            <p className="text-gray-500 text-sm">{error}</p>
          </div>
          <button
            onClick={refetch}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  const sparkValues = STATUS_ORDER.map((s) => ({
    status: s,
    label: STATUS_META[s].label,
    value: analytics.counts[s],
    color: STATUS_PALETTE[s],
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Visão Geral do Estoque</h1>
          <p className="text-gray-500 text-sm">
            {activeProducts.length} produtos monitorados &middot; Lead time médio {averageLeadTime.toFixed(1)}d &middot; Cobertura alvo {settings.diasCoberturaAlvo}d
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {hasMultipleCNPJs && (
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2.5 px-4 py-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700 shadow-sm min-w-[260px]"
              >
                <Building2 className="w-4 h-4 text-teal-600 flex-shrink-0" />
                <span className="truncate">
                  {selectedCNPJ ? formatCNPJ(selectedCNPJ) : 'Todos os CNPJs'}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 ml-auto flex-shrink-0 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {dropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setDropdownOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-40 w-full min-w-[320px] bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-64 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCNPJ(null);
                        setDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left transition-colors ${selectedCNPJ === null ? 'bg-teal-50 text-teal-900' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      <span className="flex items-center gap-3">
                        <Building2 className={`h-4 w-4 ${selectedCNPJ === null ? 'text-teal-600' : 'text-gray-400'}`} />
                        <span>
                          <strong className="block text-sm">Todos os CNPJs</strong>
                          <span className="text-xs text-gray-500">{allProducts.length} produtos monitorados</span>
                        </span>
                      </span>
                    </button>
                    {groups.map((group) => {
                      const isSelected = group.cnpj === selectedCNPJ;
                      return (
                        <button
                          key={group.cnpj}
                          onClick={() => {
                            setSelectedCNPJ(group.cnpj);
                            setDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                            isSelected
                              ? 'bg-teal-50 text-teal-900'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <Building2 className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-teal-600' : 'text-gray-400'}`} />
                          <div className="min-w-0">
                            <p className={`text-sm font-mono font-medium truncate ${isSelected ? 'text-teal-900' : 'text-gray-900'}`}>
                              {formatCNPJ(group.cnpj)}
                            </p>
                            {group.razaoSocial && (
                              <p className="text-xs text-gray-500 truncate">{group.razaoSocial}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-0.5">
                              {group.products.length} {group.products.length === 1 ? 'produto' : 'produtos'}
                            </p>
                          </div>
                          {isSelected && (
                            <div className="ml-auto flex-shrink-0 w-2 h-2 rounded-full bg-teal-500" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
          <button
            onClick={refetch}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700 shadow-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </button>
        </div>
      </div>

      {hasMultipleCNPJs && selectedGroup && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-teal-50 border border-teal-100 rounded-lg">
          <Building2 className="w-4 h-4 text-teal-600" />
          <span className="text-sm text-teal-800">
            Exibindo dados de <strong className="font-mono">{formatCNPJ(selectedGroup.cnpj)}</strong>
            {selectedGroup.razaoSocial && <span className="text-teal-600"> - {selectedGroup.razaoSocial}</span>}
          </span>
        </div>
      )}

      <ExecutiveSummary
        analytics={analytics}
        headline={executiveHeadline}
        averageLeadTime={averageLeadTime}
        targetCoverage={settings.diasCoberturaAlvo}
        scopeLabel={selectedCNPJ ? formatCNPJ(selectedCNPJ) : `${groups.length} CNPJs consolidados`}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="relative bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <CardHelp content="Ordena os produtos pelos menores dias de cobertura. Cobertura = estoque atual dividido pelo consumo médio diário." className="absolute right-3 top-3" />
          <button type="button" onClick={() => openAnalysis('coverage')} className="mb-1 flex w-full items-center justify-between pr-7 text-left">
            <h3 className="text-sm font-semibold text-gray-700">Menor Cobertura (dias)</h3>
            <span className="text-xs font-semibold text-teal-600 hover:text-teal-700">Ver análise completa</span>
          </button>
          <p className="text-xs text-gray-400 mb-4">Produtos com menos dias de cobertura por estabelecimento</p>
          {analytics.topLowCoverage.length > 0 ? (
            <HorizontalBarChart
              items={analytics.topLowCoverage.map((p) => ({
                label: p.nome,
                image: p.foto,
                ean: p.ean,
                meta: p.scopeCount > 1 ? `${p.scopeCount} CNPJs` : undefined,
                details: {
                  status: p.status, statusLabel: p.statusLabel, estoqueAtual: p.estoqueAtual,
                  cmd: p.cmd, cobertura: p.cobertura, leadTime: p.leadTime,
                  estoqueAlvo: p.estoqueAlvo, pedidoSugerido: p.pedidoSugerido, fornecedor: p.fornecedor,
                  scopeLabel: p.scopeCount > 1 ? `Consolidado em ${p.scopeCount} CNPJs` : undefined,
                },
                value: p.cobertura,
                color: p.cobertura < (p.leadTime ?? settings.leadTime)
                  ? '#dc2626'
                  : p.cobertura < settings.diasCoberturaAlvo
                    ? '#ca8a04'
                    : '#16a34a',
              }))}
              formatValue={(v) => `${v.toFixed(1)}d`}
            />
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">Sem dados de cobertura disponíveis</p>
          )}
        </div>

        <div className="relative bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <CardHelp content="Lista os produtos com maior necessidade de reposição. Pedido sugerido = estoque alvo menos estoque atual, nunca menor que zero." className="absolute right-3 top-3" />
          <button type="button" onClick={() => openAnalysis('orders')} className="mb-1 flex w-full items-center justify-between pr-7 text-left">
            <h3 className="text-sm font-semibold text-gray-700">Maiores Pedidos Sugeridos</h3>
            <span className="text-xs font-semibold text-teal-600 hover:text-teal-700">Ver análise completa</span>
          </button>
          <p className="text-xs text-gray-400 mb-4">{selectedCNPJ ? 'Produtos que mais precisam de reposição' : 'Pedidos somados por EAN em todos os CNPJs'}</p>
          {analytics.topHighOrder.length > 0 ? (
            <HorizontalBarChart
              items={analytics.topHighOrder.map((p) => ({
                label: p.nome,
                image: p.foto,
                ean: p.ean,
                meta: p.scopeCount > 1 ? `${p.scopeCount} CNPJs` : undefined,
                details: {
                  status: p.status, statusLabel: p.statusLabel, estoqueAtual: p.estoqueAtual,
                  cmd: p.cmd, cobertura: p.cobertura, leadTime: p.leadTime,
                  estoqueAlvo: p.estoqueAlvo, pedidoSugerido: p.pedidoSugerido, fornecedor: p.fornecedor,
                  scopeLabel: p.scopeCount > 1 ? `Consolidado em ${p.scopeCount} CNPJs` : undefined,
                },
                value: p.pedidoSugerido,
                color: STATUS_PALETTE[p.status],
              }))}
              formatValue={(v) => `${formatUnits(v)} un`}
            />
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">Nenhum pedido sugerido</p>
          )}
        </div>
      </div>
      <section>
        <div className="mb-3 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-600">{'Ações prioritárias'}</p>
            <h2 className="mt-1 text-lg font-bold text-slate-900">Onde atuar primeiro</h2>
          </div>
          <p className="hidden text-xs text-slate-400 sm:block">Indicadores calculados sobre o filtro atual</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <ActionInsightCard title="Sem estoque com consumo" value={analytics.coverageBuckets[1].value} description="Reposição necessária para demanda ativa" help="Conta produtos com consumo registrado e estoque atual igual a zero; corresponde exatamente à faixa Sem estoque." icon={<AlertCircle className="h-5 w-5" />} tone="red" onClick={() => openAnalysis('coverage', 'stockout')} />
          <ActionInsightCard title="Déficit para o estoque alvo" value={analytics.stockGap} description="Unidades necessárias para atingir o nível planejado" help="Soma, por produto, a diferença positiva entre estoque alvo e estoque atual." icon={<Target className="h-5 w-5" />} tone="teal" onClick={() => openAnalysis('orders')} suffix=" un" />
          <ActionInsightCard title="Unidades em excesso" value={analytics.excessUnits} description="Estoque acima do alvo calculado por produto" help="Soma, por produto, apenas o estoque atual que ultrapassa o estoque alvo." icon={<Layers3 className="h-5 w-5" />} tone="blue" suffix=" un" />
        </div>
      </section>
      <StatusDistribution values={sparkValues} onSelect={openStatusAnalysis} />

      <div className="grid gap-6 xl:grid-cols-5">
        <div className="xl:col-span-3">
          <CoverageDistribution buckets={analytics.coverageBuckets} total={activeProducts.length} targetCoverage={settings.diasCoberturaAlvo} onSelect={(filter) => openAnalysis('coverage', filter)} />
        </div>
        <div className="xl:col-span-2">
          <SupplierRiskPanel suppliers={analytics.supplierInsights} onSelect={(supplier) => openAnalysis('coverage', 'all', supplier)} />
        </div>
      </div>
      {!selectedCNPJ && cnpjPerformance.length > 1 && (
        <CnpjComparison
          rows={cnpjPerformance}
          onSelect={(cnpj) => setSelectedCNPJ(cnpj)}
        />
      )}
      {analysisRequest && (
        <InventoryAnalysisModal
          key={`${analysisRequest.mode}-${analysisRequest.initialFilter}-${analysisRequest.initialSupplier || 'all'}-${analysisRequest.initialStatuses?.join('-') || 'all-statuses'}`}
          mode={analysisRequest.mode}
          products={analysisProducts}
          targetCoverage={settings.diasCoberturaAlvo}
          initialFilter={analysisRequest.initialFilter}
          initialSupplier={analysisRequest.initialSupplier}
          initialStatuses={analysisRequest.initialStatuses}
          scopeLabel={analysisRequest.scopeLabel}
          onClose={() => setAnalysisRequest(null)}
        />
      )}
    </div>
  );
};

function ExecutiveSummary({analytics, headline, averageLeadTime, targetCoverage, scopeLabel}: {
  analytics: DashboardAnalytics;
  headline: string;
  averageLeadTime: number;
  targetCoverage: number;
  scopeLabel: string;
}) {
  const scoreColor = analytics.operationalScore >= 75 ? '#059669' : analytics.operationalScore >= 50 ? '#d97706' : '#dc2626';
  const scoreLabel = analytics.operationalScore >= 75 ? 'Operação equilibrada' : analytics.operationalScore >= 50 ? 'Atenção necessária' : 'Risco operacional alto';

  return (
    <section className="relative overflow-hidden rounded-2xl border border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-blue-50 p-6 text-slate-900 shadow-sm sm:p-8">
      <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-teal-300/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 left-1/3 h-64 w-64 rounded-full bg-blue-300/20 blur-3xl" />
      <div className="relative grid gap-8 xl:grid-cols-[1fr_auto] xl:items-center">
        <div>
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white px-3 py-1.5 text-xs font-semibold text-teal-700 shadow-sm">
              <Warehouse className="h-3.5 w-3.5" /> {scopeLabel}
            </span>
            <span className="text-xs text-slate-500">Cobertura alvo: {targetCoverage} dias</span>
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-600">Resumo executivo</p>
          <h2 className="mt-2 max-w-3xl text-2xl font-bold leading-tight text-slate-950 sm:text-3xl">{headline}</h2>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
            O índice combina ruptura, risco de abastecimento, proximidade do mínimo e excesso para priorizar decisões.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <ExecutiveMetric icon={<Boxes className="h-4 w-4" />} label="Estoque atual" value={formatUnits(analytics.totalStock)} help="Soma do estoque atual de todos os produtos no filtro selecionado." />
            <ExecutiveMetric icon={<Target className="h-4 w-4" />} label="Estoque alvo" value={formatUnits(analytics.totalTarget)} help="Soma do estoque planejado de todos os produtos no filtro selecionado." />
            <ExecutiveMetric icon={<AlertTriangle className="h-4 w-4" />} label="Produtos em alerta" value={formatUnits(analytics.alertCount)} help="Produtos classificados em ruptura, ruptura iminente ou próximos do estoque mínimo." />
            <ExecutiveMetric icon={<Truck className="h-4 w-4" />} label="Lead time médio" value={`${averageLeadTime.toFixed(1)}d`} help="Média dos prazos de entrega dos fornecedores associados aos produtos." />
          </div>
        </div>
        <div className="relative flex flex-col items-center rounded-2xl border border-teal-100 bg-white/90 p-6 shadow-sm backdrop-blur-sm">
          <CardHelp content="Índice de 0 a 100 que perde pontos conforme aumentam rupturas, riscos, itens próximos do mínimo e excessos." className="absolute right-3 top-3" />
          <div className="relative flex h-40 w-40 items-center justify-center rounded-full" style={{background: `conic-gradient(${scoreColor} ${analytics.operationalScore * 3.6}deg, #dbeafe 0deg)`}}>
            <div className="flex h-[126px] w-[126px] flex-col items-center justify-center rounded-full border border-slate-100 bg-white shadow-inner">
              <span className="text-4xl font-black text-slate-950">{analytics.operationalScore}</span>
              <span className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">de 100</span>
            </div>
          </div>
          <p className="mt-4 text-sm font-bold" style={{color: scoreColor}}>{scoreLabel}</p>
          <p className="mt-1 text-center text-xs text-slate-500">{analytics.alertCount} produtos em alerta</p>
        </div>
      </div>
    </section>
  );
}

function ExecutiveMetric({icon, label, value, help}: {icon: React.ReactNode; label: string; value: string; help: string}) {
  return (
    <div className="relative rounded-xl border border-slate-200 bg-white/90 p-3 pr-9 shadow-sm">
      <CardHelp content={help} className="absolute right-2 top-2" />
      <div className="flex items-center gap-2 text-teal-600">{icon}<span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</span></div>
      <p className="mt-2 text-lg font-bold text-slate-950">{value}</p>
    </div>
  );
}

function ActionInsightCard({title, value, description, help, icon, tone, suffix = '', onClick}: {
  title: string;
  value: number;
  description: string;
  help: string;
  icon: React.ReactNode;
  tone: 'red' | 'orange' | 'teal' | 'blue';
  suffix?: string;
  onClick?: () => void;
}) {
  const tones = {
    red: 'border-red-100 bg-red-50 text-red-700',
    orange: 'border-orange-100 bg-orange-50 text-orange-700',
    teal: 'border-teal-100 bg-teal-50 text-teal-700',
    blue: 'border-blue-100 bg-blue-50 text-blue-700',
  };
  const content = (
    <>
      <div className="flex items-start justify-between gap-3">
        <span className={`rounded-xl border p-2.5 ${tones[tone]}`}>{icon}</span>
        {onClick && <ArrowRight className="mr-7 h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-teal-600" />}
      </div>
      <p className="mt-4 text-3xl font-black tabular-nums text-slate-950">{formatUnits(value)}{suffix}</p>
      <h3 className="mt-1 text-sm font-bold text-slate-800">{title}</h3>
      <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
    </>
  );
  return (
    <div className="relative h-full">
      {onClick ? (
        <button type="button" onClick={onClick} className="group h-full w-full rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-teal-200 hover:shadow-md">{content}</button>
      ) : (
        <div className="h-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">{content}</div>
      )}
      <CardHelp content={help} className="absolute right-3 top-3" />
    </div>
  );
}
function CoverageDistribution({buckets, total, targetCoverage, onSelect}: {
  buckets: DashboardAnalytics['coverageBuckets'];
  total: number;
  targetCoverage: number;
  onSelect: (filter: CoverageAnalysisFilter) => void;
}) {
  const maxValue = Math.max(...buckets.map((bucket) => bucket.value), 1);
  return (
    <section className="relative h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <CardHelp content="Agrupa os produtos por faixas de cobertura. Cobertura = estoque atual dividido pelo consumo médio diário; as faixas usam lead time e meta configurada." className="absolute right-3 top-3" />
      <div className="flex flex-wrap items-start justify-between gap-3 pr-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-teal-600">Distribuição de cobertura</p>
          <h3 className="mt-1 text-lg font-bold text-slate-900">Faixas de abastecimento</h3>
          <p className="mt-1 text-xs text-slate-500">Meta configurada em {targetCoverage} dias</p>
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600">{total} produtos</span>
      </div>
      <div className="mt-6 flex h-4 overflow-hidden rounded-full bg-slate-100">
        {buckets.map((bucket) => (
          <div
            key={bucket.key}
            title={`${bucket.label}: ${bucket.value}`}
            className="h-full transition-all first:rounded-l-full last:rounded-r-full"
            style={{width: `${total ? (bucket.value / total) * 100 : 0}%`, backgroundColor: bucket.color}}
          />
        ))}
      </div>
      <div className="mt-6 grid gap-x-6 gap-y-4 sm:grid-cols-2">
        {buckets.map((bucket) => {
          const pct = total ? (bucket.value / total) * 100 : 0;
          return (
            <div key={bucket.key} className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
              <button type="button" onClick={() => onSelect(bucket.key)} className="group min-w-0 w-full rounded-xl p-2 text-left transition hover:bg-teal-50 focus:outline-none focus:ring-2 focus:ring-teal-200" aria-label={`Ver ${bucket.value} produtos na faixa ${bucket.label}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="h-2.5 w-2.5 flex-none rounded-full" style={{backgroundColor: bucket.color}} />
                  <span className="truncate text-sm font-semibold text-slate-700 group-hover:text-teal-700">{bucket.label}</span>
                </div>
                <span className="inline-flex items-center gap-1.5 text-sm font-bold tabular-nums text-slate-950">{bucket.value} <small className="font-medium text-slate-400">({pct.toFixed(0)}%)</small><ArrowRight className="h-3.5 w-3.5 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-teal-600" /></span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full" style={{width: `${(bucket.value / maxValue) * 100}%`, backgroundColor: bucket.color}} />
              </div>
              <p className="mt-1.5 text-[11px] text-slate-400">{bucket.description}</p>
              </button>
              <CardHelp content={bucket.help} className="mt-2 flex-none" />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SupplierRiskPanel({suppliers, onSelect}: {suppliers: DashboardAnalytics['supplierInsights']; onSelect: (filterValue: string) => void}) {
  return (
    <section className="relative h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <CardHelp content="Ordena fornecedores pela proporção de produtos em ruptura, risco ou próximos do mínimo dentro do filtro atual." className="absolute right-3 top-3" />
      <div className="flex items-start justify-between gap-3 pr-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-orange-600">Fornecedores</p>
          <h3 className="mt-1 text-lg font-bold text-slate-900">Maior concentração de risco</h3>
          <p className="mt-1 text-xs text-slate-500">Clique para ver produtos e filtrar somente os alertas.</p>
        </div>
        <span className="rounded-xl bg-orange-50 p-2.5 text-orange-600"><Truck className="h-5 w-5" /></span>
      </div>
      <div className="mt-5 space-y-4">
        {suppliers.slice(0, 5).map((supplier, index) => (
          <button type="button" key={supplier.filterValue} onClick={() => onSelect(supplier.filterValue)} className="group w-full rounded-xl border border-slate-100 bg-slate-50/70 p-3 text-left transition hover:border-teal-200 hover:bg-teal-50/60 focus:outline-none focus:ring-2 focus:ring-teal-200">
            <div className="flex items-start gap-3">
              <span className="flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-white text-xs font-black text-slate-500 shadow-sm">{index + 1}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate text-sm font-bold text-slate-800" title={supplier.name}>{supplier.name}</p>
                  <ArrowRight className="h-3.5 w-3.5 flex-none text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-teal-600" />
                  <span className={`flex-none rounded-full px-2 py-0.5 text-[10px] font-bold ${supplier.alerts ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>{supplier.alerts} alertas</span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
                  <div className={`h-full rounded-full ${supplier.alertPct >= 50 ? 'bg-red-500' : supplier.alertPct >= 25 ? 'bg-orange-500' : 'bg-emerald-500'}`} style={{width: `${Math.max(2, supplier.alertPct)}%`}} />
                </div>
                <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                  <span>{supplier.products} produtos</span>
                  <span>{supplier.medianCoverage.toFixed(1)}d típica</span>
                  <span>{formatUnits(supplier.suggested)} un sugeridas</span>
                </div>
              </div>
            </div>
          </button>
        ))}
        {!suppliers.length && <p className="py-10 text-center text-sm text-slate-400">Nenhum fornecedor encontrado no filtro atual.</p>}
      </div>
    </section>
  );
}
function CnpjComparison({rows, onSelect}: {rows: CnpjPerformance[]; onSelect: (cnpj: string) => void}) {
  return (
    <section className="relative overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm">
      <CardHelp content="Compara os principais indicadores operacionais por estabelecimento. Clique em um CNPJ para filtrar toda a visão geral." className="absolute right-3 top-3" />
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-blue-100 bg-gradient-to-r from-white via-blue-50/40 to-teal-50/50 px-6 py-5 pr-12">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Comparativo por estabelecimento</p>
          <h3 className="mt-1 text-lg font-bold text-slate-900">Desempenho dos CNPJs</h3>
          <p className="mt-1 text-xs text-slate-500">Indicadores principais, priorizados por itens sem estoque com consumo</p>
        </div>
        <span className="mr-7 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 shadow-sm">
          <Building2 className="h-3.5 w-3.5" /> {rows.length} estabelecimentos
        </span>
      </div>
      <div className="space-y-3 bg-slate-50/70 p-3 sm:p-4">
        {rows.map((row) => (
          <button
            type="button"
            key={row.cnpj}
            onClick={() => onSelect(row.cnpj)}
            className="group relative grid w-full grid-cols-2 gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-teal-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-teal-300 sm:px-5 xl:grid-cols-[minmax(230px,1.2fr)_minmax(120px,0.55fr)_minmax(145px,0.65fr)_minmax(145px,0.65fr)_minmax(190px,0.85fr)_24px] xl:items-center xl:gap-4"
          >
              <span className="col-span-2 flex min-w-0 items-center gap-3 pr-8 xl:col-span-1 xl:pr-0">
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition group-hover:bg-blue-600 group-hover:text-white">
                  <Building2 className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                <strong className="block truncate font-mono text-sm text-slate-900">{formatCNPJ(row.cnpj)}</strong>
                <small className="mt-0.5 block truncate text-xs text-slate-400">{row.razaoSocial || 'Razão social não informada'}</small>
                </span>
              </span>
              <CnpjMetric label="Produtos" value={formatUnits(row.products)} icon={<Boxes className="h-4 w-4" />} tone="slate" />
              <CnpjMetric label="Estoque atual" value={`${formatUnits(row.stock)} un`} icon={<Warehouse className="h-4 w-4" />} tone="teal" />
              <CnpjMetric label="Estoque alvo" value={`${formatUnits(row.targetStock)} un`} icon={<Target className="h-4 w-4" />} tone="blue" />
              <CnpjMetric
                label="Sem estoque com consumo"
                value={formatUnits(row.stockoutWithConsumption)}
                detail={`${row.stockoutPct.toFixed(0)}% dos produtos`}
                icon={<AlertCircle className="h-4 w-4" />}
                tone={row.stockoutWithConsumption ? 'red' : 'emerald'}
              />
              <ArrowRight className="absolute right-5 top-5 h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-teal-600 xl:static" />
          </button>
        ))}
      </div>
    </section>
  );
}

const CNPJ_METRIC_TONES = {
  slate: 'border-slate-200 bg-slate-50 text-slate-700',
  teal: 'border-teal-100 bg-teal-50/70 text-teal-700',
  blue: 'border-blue-100 bg-blue-50/70 text-blue-700',
  red: 'border-red-100 bg-red-50/70 text-red-700',
  emerald: 'border-emerald-100 bg-emerald-50/70 text-emerald-700',
} as const;

function CnpjMetric({label, value, detail, icon, tone}: {
  label: string;
  value: string;
  detail?: string;
  icon: React.ReactNode;
  tone: keyof typeof CNPJ_METRIC_TONES;
}) {
  return (
    <span className={`flex min-w-0 flex-col rounded-xl border p-3 ${CNPJ_METRIC_TONES[tone]}`}>
      <span className="flex items-center gap-2">
        <span className="rounded-lg bg-white/90 p-1.5 shadow-sm">{icon}</span>
        <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      </span>
      <span className="mt-2 flex min-w-0 items-baseline justify-between gap-2">
        <strong className="truncate text-base font-black tabular-nums">{value}</strong>
        {detail && <small className="flex-none text-[10px] font-semibold opacity-75">{detail}</small>}
      </span>
    </span>
  );
}
